import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { ValidationError } from '../components/bulkLoading';
import {
  GET_MY_BULK_UPLOAD_JOBS,
  CANCEL_BULK_UPLOAD_JOB,
  RETRY_BULK_UPLOAD_JOB,
  DELETE_BULK_UPLOAD_JOB,
} from '../containers/treasury/accountingTransactionRecord/queries';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BackgroundTaskProgress {
  isActive: boolean;
  current: number;
  total: number;
  percentage: number;
}

export interface BackgroundTaskResult {
  successCount: number;
  errorCount: number;
  errors: ValidationError[];
  fileName: string;
}

export interface BackgroundTask {
  id: string;
  jobId: string;
  title: string;
  fileName: string;
  progress: BackgroundTaskProgress;
  status: 'running' | 'completed' | 'error';
  result?: BackgroundTaskResult;
  createdAt: string;
  lastProgressAt?: string;
  detailsRoute?: string;
}

interface BackgroundTasksContextType {
  tasks: BackgroundTask[];
  hasActiveTasks: boolean;
  hasUnseenCompleted: boolean;
  activeTaskCount: number;
  addTask: (task: BackgroundTask) => void;
  updateTaskProgress: (id: string, progress: BackgroundTaskProgress) => void;
  completeTask: (id: string, result: BackgroundTaskResult) => void;
  failTask: (id: string, errorMessage: string) => void;
  removeTask: (id: string) => void;
  cancelTask: (id: string) => Promise<void>;
  retryTask: (id: string) => Promise<void>;
  clearCompleted: () => void;
  markSeen: () => void;
  isTaskCancelled: (id: string) => boolean;
  refreshTasks: () => Promise<void>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DISMISSED_KEY = 'bg_tasks_dismissed';
const DETAILS_ROUTE = '/tesoreria/movimientos';
const POLLING_INTERVAL = 5000;
const MAX_POLL_FAILURES = 3;

const BackgroundTasksContext = createContext<BackgroundTasksContextType | null>(null);

// ─── Helpers ────────────────────────────────────────────────────────────────

const loadDismissedIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
  } catch {
    return new Set();
  }
};

const saveDismissedIds = (ids: Set<string>) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // Silently fail
  }
};

interface ServerJob {
  jobId: string;
  status: string;
  fileName: string | null;
  progress: { current: number; total: number; percentage: number } | null;
  result: {
    totalRows: number;
    savedCount: number;
    errorCount: number;
    fileName: string;
  } | null;
  createdAt: string;
}

const mapServerJobToTask = (job: ServerJob): BackgroundTask => {
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const progress = job.progress || { current: 0, total: 0, percentage: 0 };

  let status: BackgroundTask['status'] = 'running';
  if (isCompleted) status = 'completed';
  if (isFailed) status = 'error';

  let result: BackgroundTaskResult | undefined;
  if ((isCompleted || isFailed) && job.result) {
    result = {
      successCount: job.result.savedCount || 0,
      errorCount: job.result.errorCount || 0,
      errors: [],
      fileName: job.result.fileName || job.fileName || '',
    };
  }

  return {
    id: `bg-task-${job.jobId}`,
    jobId: job.jobId,
    title: 'Carga Masiva de Movimientos',
    fileName: job.fileName || '',
    progress: {
      isActive: status === 'running',
      current: progress.current,
      total: progress.total,
      percentage: progress.percentage,
    },
    status,
    result,
    createdAt: job.createdAt,
    detailsRoute: DETAILS_ROUTE,
  };
};

// ─── Provider ───────────────────────────────────────────────────────────────

export const BackgroundTasksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const [unseenCompleted, setUnseenCompleted] = useState<Set<string>>(new Set());
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const dismissedIdsRef = useRef(loadDismissedIds());
  const pollFailCountRef = useRef(0);
  /** IDs registrados explícitamente via addTask (enviados a segundo plano) */
  const bgRegisteredIdsRef = useRef<Set<string>>(new Set());

  // Queries & Mutations
  const [fetchJobs] = useLazyQuery(GET_MY_BULK_UPLOAD_JOBS, { fetchPolicy: 'network-only' });
  const [cancelJobMutation] = useMutation(CANCEL_BULK_UPLOAD_JOB);
  const [retryJobMutation] = useMutation(RETRY_BULK_UPLOAD_JOB);
  const [deleteJobMutation] = useMutation(DELETE_BULK_UPLOAD_JOB);

  // ─── Sync from server ─────────────────────────────────────────────────

  const syncFromServer = useCallback(async (isPolling = false) => {
    try {
      const { data } = await fetchJobs();
      const serverJobs: ServerJob[] = data?.getMyBulkUploadJobs || [];
      pollFailCountRef.current = 0;

      const mappedTasks = serverJobs
        .map(mapServerJobToTask)
        .filter((t) => !dismissedIdsRef.current.has(t.id));

      // Detectar transiciones running -> completed/error ANTES de tocar el estado y notificar
      // aquí, fuera del updater de setTasks: React (StrictMode) puede invocar una función
      // updater más de una vez, así que un toast disparado dentro de ella se duplicaría.
      if (isPolling) {
        const prevTasks = tasksRef.current;
        const newlyCompleted: string[] = [];
        mappedTasks.forEach((serverTask) => {
          const existing = prevTasks.find((t) => t.id === serverTask.id);
          const isBgTask = bgRegisteredIdsRef.current.has(serverTask.id);
          if (isBgTask && existing?.status === 'running' && serverTask.status !== 'running') {
            if (serverTask.status === 'completed') {
              const hasErrors = serverTask.result && serverTask.result.errorCount > 0;
              if (hasErrors) {
                toast.warning(
                  `"${serverTask.fileName}": ${serverTask.result!.successCount} guardado(s), ${serverTask.result!.errorCount} error(es)`
                );
              } else {
                toast.success(`"${serverTask.fileName}" completada exitosamente`);
              }
              newlyCompleted.push(serverTask.id);
            } else if (serverTask.status === 'error') {
              toast.error(`"${serverTask.fileName}" falló durante el procesamiento`);
              newlyCompleted.push(serverTask.id);
            }
          }
        });
        if (newlyCompleted.length > 0) {
          setUnseenCompleted((s) => new Set([...s, ...newlyCompleted]));
        }
      }

      setTasks((prev) => {
        // Mantener tasks locales que no vinieron del servidor (ej: recién creadas)
        const serverIds = new Set(mappedTasks.map((t) => t.id));
        const localOnly = prev.filter((t) => !serverIds.has(t.id) && t.status === 'running');

        return [...localOnly, ...mappedTasks];
      });

      // Mark unseen on initial load
      if (!isPolling) {
        const completedIds = mappedTasks
          .filter((t) => t.status === 'completed' || t.status === 'error')
          .map((t) => t.id);
        if (completedIds.length > 0) {
          setUnseenCompleted(new Set(completedIds));
        }
      }
    } catch {
      pollFailCountRef.current++;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load on mount
  useEffect(() => {
    syncFromServer(false);
  }, [syncFromServer]);

  // ─── Global polling ───────────────────────────────────────────────────

  const hasActiveTasks = tasks.some((t) => t.status === 'running');

  useEffect(() => {
    if (!hasActiveTasks) {
      pollFailCountRef.current = 0;
      return undefined;
    }

    const interval = setInterval(() => {
      if (pollFailCountRef.current >= MAX_POLL_FAILURES) {
        clearInterval(interval);
        return;
      }
      syncFromServer(true);
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [hasActiveTasks, syncFromServer]);

  // ─── Task CRUD ────────────────────────────────────────────────────────

  const addTask = useCallback((task: BackgroundTask) => {
    if (dismissedIdsRef.current.has(task.id)) return;
    bgRegisteredIdsRef.current.add(task.id);
    setTasks((prev) => {
      if (prev.some((t) => t.id === task.id)) return prev;
      return [task, ...prev];
    });
  }, []);

  const updateTaskProgress = useCallback((id: string, progress: BackgroundTaskProgress) => {
    if (dismissedIdsRef.current.has(id)) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, progress, lastProgressAt: new Date().toISOString() } : t
      )
    );
  }, []);

  const completeTask = useCallback((id: string, result: BackgroundTaskResult) => {
    if (dismissedIdsRef.current.has(id)) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              status: 'completed' as const,
              result,
              progress: {
                isActive: false,
                current: result.successCount + result.errorCount,
                total: result.successCount + result.errorCount,
                percentage: 100,
              },
            }
          : t
      )
    );
    setUnseenCompleted((prev) => new Set([...prev, id]));
  }, []);

  const failTask = useCallback((id: string, errorMessage: string) => {
    if (dismissedIdsRef.current.has(id)) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              status: 'error' as const,
              result: {
                successCount: 0,
                errorCount: 1,
                errors: [
                  {
                    id: 'bg-error',
                    row: 0,
                    column: 'SISTEMA',
                    columnIndex: -1,
                    value: null,
                    errorType: 'invalid' as const,
                    message: errorMessage,
                    originalData: {},
                  },
                ],
                fileName: t.fileName,
              },
            }
          : t
      )
    );
    setUnseenCompleted((prev) => new Set([...prev, id]));
  }, []);

  const removeTask = useCallback((id: string) => {
    const task = tasksRef.current.find((t) => t.id === id);
    dismissedIdsRef.current.add(id);
    saveDismissedIds(dismissedIdsRef.current);
    setTasks((prev) => prev.filter((t) => t.id !== id));
    setUnseenCompleted((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

    // Soft-delete in backend
    if (task?.jobId) {
      deleteJobMutation({ variables: { jobId: task.jobId } }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Cancel & Retry ───────────────────────────────────────────────────

  const cancelTask = useCallback(
    async (id: string) => {
      const task = tasksRef.current.find((t) => t.id === id);
      if (!task?.jobId) return;

      try {
        await cancelJobMutation({ variables: { jobId: task.jobId } });
        failTask(id, 'Cancelado por el usuario');
        toast.info(`"${task.fileName}" cancelada`);
      } catch {
        toast.error('No se pudo cancelar la carga');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [failTask]
  );

  const retryTask = useCallback(async (id: string) => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task?.jobId) return;

    try {
      const { data } = await retryJobMutation({ variables: { jobId: task.jobId } });
      if (data?.retryBulkUploadJob) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === id
              ? {
                  ...t,
                  status: 'running' as const,
                  result: undefined,
                  progress: { isActive: true, current: 0, total: 0, percentage: 0 },
                }
              : t
          )
        );
        setUnseenCompleted((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        toast.info(`Reintentando "${task.fileName}"...`);
      }
    } catch {
      toast.error('No se pudo reintentar la carga. Se alcanzó el máximo de intentos.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Bulk actions ─────────────────────────────────────────────────────

  const clearCompleted = useCallback(() => {
    setTasks((prev) => {
      prev
        .filter((t) => t.status !== 'running')
        .forEach((t) => {
          dismissedIdsRef.current.add(t.id);
          if (t.jobId) {
            deleteJobMutation({ variables: { jobId: t.jobId } }).catch(() => {});
          }
        });
      saveDismissedIds(dismissedIdsRef.current);
      return prev.filter((t) => t.status === 'running');
    });
    setUnseenCompleted(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSeen = useCallback(() => {
    setUnseenCompleted(new Set());
  }, []);

  const isTaskCancelled = useCallback((id: string) => dismissedIdsRef.current.has(id), []);

  const refreshTasks = useCallback(async () => {
    pollFailCountRef.current = 0;
    await syncFromServer(false);
  }, [syncFromServer]);

  // ─── Derived state ────────────────────────────────────────────────────

  const hasUnseenCompleted = unseenCompleted.size > 0;
  const activeTaskCount = tasks.filter((t) => t.status === 'running').length;

  const contextValue = useMemo(
    () => ({
      tasks,
      hasActiveTasks,
      hasUnseenCompleted,
      activeTaskCount,
      addTask,
      updateTaskProgress,
      completeTask,
      failTask,
      removeTask,
      cancelTask,
      retryTask,
      clearCompleted,
      markSeen,
      isTaskCancelled,
      refreshTasks,
    }),
    [
      tasks,
      hasActiveTasks,
      hasUnseenCompleted,
      activeTaskCount,
      addTask,
      updateTaskProgress,
      completeTask,
      failTask,
      removeTask,
      cancelTask,
      retryTask,
      clearCompleted,
      markSeen,
      isTaskCancelled,
      refreshTasks,
    ]
  );

  return (
    <BackgroundTasksContext.Provider value={contextValue}>
      {children}
    </BackgroundTasksContext.Provider>
  );
};

export const useBackgroundTasks = (): BackgroundTasksContextType => {
  const ctx = useContext(BackgroundTasksContext);
  if (!ctx) {
    throw new Error('useBackgroundTasks must be used within BackgroundTasksProvider');
  }
  return ctx;
};
